import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, CircleCheck, Flag, Trophy } from 'lucide-react';
import { useParams } from 'react-router-dom';
import {
  computeStablefordStandings,
  type ScorecardResponse,
} from '@golf-app/shared';

import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';

function completeHole(scorecard: ScorecardResponse, holeNumber: number): boolean {
  const saved = scorecard.holeScores.find((row) => row.holeNumber === holeNumber);
  if (!saved) return false;
  const ids = new Set(saved.scores.map((score) => score.playerId));
  return scorecard.players.every((player) => ids.has(player.playerId));
}

function scoreDrafts(scorecard: ScorecardResponse, holeNumber: number): Record<string, string> {
  const saved = scorecard.holeScores.find((row) => row.holeNumber === holeNumber);
  const byPlayer = new Map(saved?.scores.map((score) => [score.playerId, score.gross]) ?? []);
  const par = scorecard.course.holes.find((hole) => hole.holeNumber === holeNumber)?.par ?? 4;
  return Object.fromEntries(scorecard.players.map((player) => [
    player.playerId,
    String(byPlayer.get(player.playerId) ?? par),
  ]));
}

export default function ScorecardRound() {
  const { roundId } = useParams<{ roundId: string }>();
  const [scorecard, setScorecard] = useState<ScorecardResponse | null>(null);
  const [holeNumber, setHoleNumber] = useState<number | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadScorecard() {
    if (!roundId) return;
    setLoading(true);
    try {
      const next = await api.getScorecard(roundId);
      setScorecard(next);
      setHoleNumber((current) => current ?? next.course.holes[0]?.holeNumber ?? null);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load this scorecard.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadScorecard();
  }, [roundId]);

  useEffect(() => {
    if (!scorecard || holeNumber == null) return;
    setDrafts(scoreDrafts(scorecard, holeNumber));
  }, [scorecard, holeNumber]);

  const holes = scorecard?.course.holes ?? [];
  const holeIndex = holeNumber == null ? -1 : holes.findIndex((hole) => hole.holeNumber === holeNumber);
  const currentHole = holeIndex >= 0 ? holes[holeIndex]! : null;
  const allHolesSaved = scorecard != null && holes.every((hole) => completeHole(scorecard, hole.holeNumber));
  const stableford = useMemo(() => {
    if (!scorecard?.stablefordConfig) return null;
    return computeStablefordStandings({
      config: scorecard.stablefordConfig,
      holes: scorecard.course.holes,
      players: scorecard.players,
      holeScores: scorecard.holeScores,
    });
  }, [scorecard]);
  const standings = useMemo(() => {
    if (!scorecard || !stableford) return [];
    return scorecard.players
      .map((player, index) => ({
        ...player,
        order: index,
        standing: stableford.standings.find((row) => row.playerId === player.playerId)!,
      }))
      .sort((left, right) => right.standing.points - left.standing.points || left.order - right.order);
  }, [scorecard, stableford]);

  async function saveHole() {
    if (!scorecard || !currentHole) return;
    setError(null);
    setSaving(true);
    try {
      const scores = scorecard.players.map((player) => {
        const gross = Number(drafts[player.playerId]);
        if (!Number.isInteger(gross) || gross < 1 || gross > 12) {
          throw new Error(`${player.nickname}'s score must be a whole number from 1 to 12.`);
        }
        return { playerId: player.playerId, gross };
      });
      await api.saveScorecardHole(scorecard.roundId, currentHole.holeNumber, scores);
      const refreshed = await api.getScorecard(scorecard.roundId);
      setScorecard(refreshed);
      const nextHole = refreshed.course.holes[holeIndex + 1]?.holeNumber;
      if (nextHole != null) setHoleNumber(nextHole);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save this hole.');
    } finally {
      setSaving(false);
    }
  }

  async function completeRound() {
    if (!scorecard) return;
    setFinishing(true);
    setError(null);
    try {
      const completed = await api.completeScorecardRound(scorecard.roundId);
      setScorecard(completed);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not complete this scorecard.');
    } finally {
      setFinishing(false);
    }
  }

  if (loading && !scorecard) {
    return <AppShell back={{ to: '/app/home', label: 'Home' }}><div className="t-loading"><span className="spinner" /></div></AppShell>;
  }

  if (!scorecard || !currentHole) {
    return (
      <AppShell back={{ to: '/app/home', label: 'Home' }}>
        <section className="t-hero"><h1>Scorecard unavailable</h1><p>{error ?? 'This round could not be found.'}</p></section>
        <button className="t-btn t-btn-secondary" type="button" onClick={() => void loadScorecard()}>Try again</button>
      </AppShell>
    );
  }

  const completed = scorecard.status === 'complete';

  return (
    <AppShell back={{ to: '/app/home', label: 'Home' }}>
      <section className="t-hero scorecard-round-hero">
        <div>
          <span className="scorecard-round-kicker">{completed ? 'Completed scorecard' : 'Live scorecard'}</span>
          <h1>{scorecard.courseName ?? 'Current round'}</h1>
          <p>{scorecard.teeName ?? scorecard.course.tee.name ?? 'Tee'} · {holes.length} holes</p>
        </div>
        <Flag aria-hidden="true" />
      </section>
      {error ? <div className="t-error" role="alert">{error}</div> : null}

      {scorecard.stablefordConfig && stableford ? (
        <section className="scorecard-standings" aria-label="Stableford standings">
          <div className="scorecard-standings-heading">
            <div>
              <span className="t-label">Stableford</span>
              <p>{scorecard.stablefordConfig.scoringBasis === 'net' ? 'Net points' : 'Gross points'} · {scorecard.stablefordConfig.points.albatrossOrBetter}/{scorecard.stablefordConfig.points.eagle}/{scorecard.stablefordConfig.points.birdie}/{scorecard.stablefordConfig.points.par}/{scorecard.stablefordConfig.points.bogey}/{scorecard.stablefordConfig.points.doubleBogeyOrWorse}</p>
            </div>
            <Trophy aria-hidden="true" />
          </div>
          <div className="scorecard-standings-list">
            {standings.map((player, index) => (
              <div className="scorecard-standing-row" key={player.playerId}>
                <span className="scorecard-standing-rank">{index + 1}</span>
                <span className="scorecard-standing-name">{player.nickname}</span>
                <span className="scorecard-standing-holes">{player.standing.holesPlayed} played</span>
                <strong>{player.standing.points}</strong>
                <span className="scorecard-standing-points">pts</span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="scorecard-entry">
        <div className="scorecard-hole-nav">
          <button type="button" className="scorecard-nav-button" disabled={holeIndex <= 0} onClick={() => setHoleNumber(holes[holeIndex - 1]!.holeNumber)} aria-label="Previous hole"><ChevronLeft aria-hidden="true" /></button>
          <div>
            <span className="t-label">Hole {currentHole.holeNumber} of {holes.length}</span>
            <strong>Par {currentHole.par}</strong>
            <span>HCP {currentHole.strokeIndex}</span>
          </div>
          <button type="button" className="scorecard-nav-button" disabled={holeIndex >= holes.length - 1} onClick={() => setHoleNumber(holes[holeIndex + 1]!.holeNumber)} aria-label="Next hole"><ChevronRight aria-hidden="true" /></button>
        </div>
        <div className="scorecard-score-list">
          {scorecard.players.map((player) => {
            const holePoints = stableford?.holePointsByPlayer[player.playerId]?.[currentHole.holeNumber];
            return (
              <label className="scorecard-score-row" key={player.playerId}>
                <span className="scorecard-score-name">{player.nickname}</span>
                {holePoints != null ? <span className="scorecard-score-points">{holePoints} pts</span> : null}
                <input
                  className="scorecard-score-input"
                  value={drafts[player.playerId] ?? ''}
                  onChange={(event) => setDrafts((current) => ({ ...current, [player.playerId]: event.target.value }))}
                  inputMode="numeric"
                  maxLength={2}
                  disabled={completed}
                  aria-label={`${player.nickname} score for hole ${currentHole.holeNumber}`}
                />
              </label>
            );
          })}
        </div>
        {!completed ? (
          <button type="button" className="t-btn t-btn-primary t-btn-block" onClick={() => void saveHole()} disabled={saving}>
            {saving ? 'Saving hole…' : completeHole(scorecard, currentHole.holeNumber) ? 'Update hole' : 'Save hole'}
          </button>
        ) : null}
      </section>

      {!completed && allHolesSaved && scorecard.canComplete ? (
        <button type="button" className="t-btn t-btn-secondary scorecard-complete-button" onClick={() => void completeRound()} disabled={finishing}>
          <CircleCheck aria-hidden="true" /> {finishing ? 'Completing round…' : 'Complete scorecard'}
        </button>
      ) : null}
    </AppShell>
  );
}
